import React from 'react';
import { createPortal } from 'react-dom';
import Modal from '@/components/Modal/modal';
import EmailLink from '@/components/EmailLink';
import { StatusSummaryResponse, SyncRunModel } from '@/models/sharepoint';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  topic: 'instructions' | 'settings';
  summary?: StatusSummaryResponse;
  syncRuns?: SyncRunModel[];
  isLoading?: boolean;
};

const SharepointModal = ({
  isOpen,
  onClose,
  topic,
  summary,
  syncRuns,
  isLoading,
}: Props) => {
  return createPortal(
    <Modal
      title={
        topic === 'instructions'
          ? 'Connect to SharePoint'
          : 'SharePoint Settings'
      }
      isOpen={isOpen}
      onClose={onClose}
      size="medium"
    >
      {topic === 'instructions' ? (
        <div>
          <p>
            To connect SharePoint to your Workspace, please send an email to{' '}
            <EmailLink /> with the following information:
          </p>
          <ul>
            <li>The email address associated with your SharePoint account.</li>
            <li>The email address of your account in our Workspace.</li>
            <li>The URL of your SharePoint site or tenant.</li>
            <li>
              The type of data you intend to sync (e.g., documents,
              spreadsheets, etc).
            </li>
            <li>
              A rough estimate of the number of files and total size you plan to
              sync from SharePoint.
            </li>
          </ul>
          <p>
            Our team will reach out to you with the next steps to complete the
            connection process.
          </p>
        </div>
      ) : (
        <div>
          <p>SharePoint is currently connected.</p>
          <ul>
            <li>
              Last Synced:{' '}
              <strong>
                {summary?.last_sync
                  ? new Date(summary.last_sync).toLocaleDateString()
                  : '—'}
              </strong>
            </li>
            <li>
              Processing files:{' '}
              <strong>{summary?.by_ingest_status?.processing ?? 0}</strong>
            </li>
            <li>
              Total files: <strong>{summary?.total_files ?? 0}</strong>
            </li>
          </ul>

          <h4>Recent Sync Runs</h4>
          {isLoading ? (
            <p>Loading sync runs...</p>
          ) : syncRuns && syncRuns.length > 0 ? (
            <ul>
              {syncRuns.slice(0, 5).map((run) => (
                <li key={run.mongodb_document_id}>
                  {new Date(Number(run.timestamp)).toLocaleString()} —{' '}
                  {run.status} — {run.files_downloaded} downloaded
                </li>
              ))}
            </ul>
          ) : (
            <p>No recent sync runs available.</p>
          )}

          <p>
            If you wish to disconnect SharePoint, please contact <EmailLink />{' '}
            and our team will assist you.
          </p>
        </div>
      )}
    </Modal>,
    document.body
  );
};

export default SharepointModal;
